import React, { useEffect, useState, useCallback, useMemo, memo } from "react";
import "./ToggleSwitch.css";
import { Button, Row } from "react-bootstrap";
import "./MyMachinePage.css";
import { useAuth } from "../../AuthContext";
import instance, { apiEncryptRequest, decryptData } from "../../Api";
import { useNavigate } from "react-router-dom";
import Loader from "../common/Loader";
import { FaX } from "react-icons/fa6";
import toast, { Toaster } from "react-hot-toast";
import AppToast from "../../AppToast";
import { useDropdown } from "../Header";

// ─── Constants (defined once, never recreated) ────────────────────────────────
const PROCESSING_MSG = "Server configuration in Progress.\nPlease Return after 15 Minutes from the Creation Time...";
const STD_CATS = [1, 5, 6];
const CPU_CATS = [3, 7, 8];
const RAM_CATS = [4, 9, 10];

// ─── Pure helpers (outside component — zero re-creation cost) ─────────────────
const isMobileDevice = () => window.matchMedia("(max-width: 800px)").matches;

const getVmLabel = (item) => {
  const prefix = item.vm_type === 1 ? "PortGate " : "Cloud ";
  const c = item.vm_config_category;
  if (STD_CATS.includes(c)) return `${prefix}Standard`;
  if (CPU_CATS.includes(c)) return `${prefix}CPU Pro`;
  if (RAM_CATS.includes(c)) return `${prefix}RAM Pro`;
  return "";
};

const getDiskSize = (item) => {
  if (item.disk_type === "hdd") return item.hard_disk;
  if (item.disk_type === "ssd") return item.ssd;
  return item.nvme;
};

const showAppToast = (msg, isMobile) =>
  toast((t) => <AppToast id={t.id} message={msg} isMobile={isMobile} />);

// ─── Memoized sub-components (only re-render when their own props change) ─────

const LoaderOverlay = memo(() => (
  <div className="loading-overlay" style={{ zIndex: "99999999999999" }}>
    <Loader isLoading />
  </div>
));

const IpRow = memo(({ label, value }) =>
  value !== null ? (
    <div className="machine-ip" style={{ marginTop: "8px" }}>{label}: {value}</div>
  ) : (
    <div className="machine-ip" style={{ marginTop: "10px", display: "flex", marginBottom: "-10px" }}>
      <span style={{ marginLeft: "-30%" }}>{label}:</span>
      <p className="loader-ip" style={{ height: "12px", marginTop: "1px", marginLeft: "5px" }} />
    </div>
  )
);

const SlaPopup = memo(({ isMobile, slaDesc, myMachineId, onClose, onAgree }) => (
  <div style={{ display: "grid", justifyItems: "center" }}>
    <div style={{
      backgroundImage: "url(/main-bg.jpg)", backgroundSize: "cover",
      top: "10%", position: "absolute", zIndex: "99",
      width: isMobile ? "90%" : "35%", backdropFilter: "blur(5px)",
      height: "35rem", boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
      borderRadius: "12px", border: "2px solid #e97730",
    }}>
      <div style={{ display: "grid", justifyItems: "center" }}>
        <button style={{ color: "#035189", position: "absolute", right: 0, border: "none", backgroundColor: "transparent", width: 20, height: 20 }} onClick={onClose}>X</button>
        <h4 style={{ marginTop: 10 }}>SLA Agreement</h4>
        <div style={{ height: "25rem", overflowY: "scroll", padding: 30, borderRadius: 8, border: "1px solid #e97730" }} id="pdfContent">
          <p>{slaDesc}</p>
        </div>
        <p />
        <button
          style={{ color: "white", width: "8rem", height: 40, backgroundColor: "#035189", borderRadius: 25, border: "2px solid #ffff", outline: "2px solid #035189", marginTop: 25 }}
          onClick={() => { onClose(); onAgree(myMachineId); }}
        >I AGREE</button>
      </div>
    </div>
  </div>
));

const JumpDeletePopup = memo(({ isMobile, onClose, onViewVmList }) => (
  <div style={{
    top: isMobile ? "95%" : "10%", left: isMobile ? "5%" : "30%",
    position: "absolute", zIndex: 99999,
    width: isMobile ? "23rem" : "50rem", backdropFilter: "blur(20px)",
    height: "30rem", boxShadow: "0px 0px 10px rgba(0,0,0,0.1)",
    borderRadius: 12, border: "2px solid #e97730", fontWeight: 600, fontSize: 17,
  }}>
    <button style={{ position: "absolute", backgroundColor: "transparent", border: "none", right: 0 }} onClick={onClose}>
      <FaX style={{ color: "#e97730", fontSize: 19 }} />
    </button>
    <div style={{ position: "relative", textAlign: "center" }}>
      <h4 style={{ color: "white", position: "absolute", top: "4rem", left: "50%", width: "90%", height: 70, transform: "translate(-50%,-50%)", padding: 20, outline: "4px solid #b71b1b", border: "4px solid #fff", borderRadius: 30, backgroundColor: "#b71b1b", fontWeight: 700, fontSize: 23 }}>
        ⚠️ Jump Server Deletion Blocked
      </h4>
      <h4 style={{ color: "white", position: "absolute", top: "16rem", left: "50%", width: "90%", transform: "translate(-50%,-50%)", padding: 20, outline: "4px solid #035189", border: "4px solid #fff", borderRadius: 30, backgroundColor: "#035189", fontWeight: 600 }}>
        🔗 This Jump Server is currently connected to a VM and cannot be deleted.<br />
        🖥️ Please remove the Jump Server from Connected VMs<br />
        🗑️ Once disconnected, you will be able to delete the Jump Server.<br />
        👉 Kindly update the VM configuration and try again.
      </h4>
    </div>
    <div style={{ marginTop: "25rem", display: "flex", justifyContent: "center", gap: 25 }}>
      {[{ label: "Go Back", onClick: onClose }, { label: "View VM List", onClick: onViewVmList }].map(({ label, onClick }) => (
        <button key={label} onClick={onClick} style={{ width: isMobile ? "10rem" : "11rem", fontSize: 18, fontWeight: 700, color: "white", height: 46, backgroundColor: "#e97730", outline: "4px solid #e97730", border: "4px solid #fff", borderRadius: 30 }}>
          {label}
        </button>
      ))}
    </div>
  </div>
));

const VncPopup = memo(({ isMobile, vNCinfo, onClose }) => (
  <div style={{ top: 0, left: 0, right: 0, position: "fixed", width: "100%", height: isMobile ? "120vh" : "100vh", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 9999999 }}>
    <div style={{ position: "relative" }}>
      <img src="/admin/images/admin/transaction-failed/transaction-failed-bg.png"
        style={{ width: "25rem", height: "15rem", position: "absolute", marginTop: isMobile ? "45%" : "20%", left: "50%", transform: "translate(-50%,-50%)" }} />
      <div style={{ position: "relative", zIndex: 1, padding: "2.5rem 1.5rem" }}>
        <div onClick={onClose}>
          <FaX style={{ position: "absolute", top: isMobile ? "15%" : "54%", right: isMobile ? "5%" : "39%", color: "#fff", fontSize: "1.5rem" }} />
        </div>
        {vNCinfo && (
          <div style={{ position: "relative", marginLeft: isMobile ? "20%" : "45%", marginTop: isMobile ? "23%" : "15%" }}>
            <span style={{ color: "#154e7a", fontSize: 24, fontWeight: 600 }}>
              PORT : {vNCinfo.port}<br />IP : {vNCinfo.ip}<br />PASSWORD : {vNCinfo.password}
            </span>
          </div>
        )}
      </div>
    </div>
  </div>
));

const EditPopup = memo(({ isMobile, onClose, onEditOs, onEditConfig }) => {
  const items = [
    { icon: "/images/admin/01-home/cpu.svg", label: "Edit OS", onClick: onEditOs },
    { icon: "/admin/images/admin/menu/vm-white.png", label: "Edit Config", onClick: onEditConfig },
  ];
  return (
    <div style={{ top: "10%", left: isMobile ? "3%" : "25%", position: "absolute", zIndex: 9, width: isMobile ? "27rem" : "70rem", backdropFilter: "blur(5px)", height: isMobile ? "20rem" : "40rem", boxShadow: "0px 0px 10px rgba(0,0,0,0.1)", borderRadius: 12, border: "2px solid #e97730" }}>
      <button style={{ zIndex: 9, position: "absolute", backgroundColor: "transparent", border: "none", right: 0 }} onClick={onClose}>
        <FaX style={{ color: "#e97730", fontSize: 19 }} />
      </button>
      <div style={{ position: "relative", marginLeft: isMobile ? "10%" : "25%", marginTop: "23%", display: "flex", flexWrap: "nowrap", flexDirection: "row", gap: isMobile ? "3%" : "20%" }}>
        {items.map(({ icon, label, onClick }) => (
          <div key={label}>
            <figure style={{ width: 100, height: 100, background: "rgb(233,119,48)", borderRadius: "50%", objectFit: "cover", margin: "auto", padding: 20, outline: "3px solid rgb(233,119,48)", border: "5px solid white" }}>
              <img src={icon} alt="" style={{ marginLeft: -5, width: 60, height: 60 }} />
            </figure>
            <button style={{ width: isMobile ? "10rem" : "12rem", marginTop: 2, zIndex: 9, position: "relative", fontWeight: 700, color: "white", height: 55, backgroundColor: "#e97730", outline: "4px solid #e97730", border: "4px solid #ffff", borderColor: "white", borderRadius: 30 }} onClick={onClick}>
              {label}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
});

const DeletePopup = memo(({ isMobile, onClose, onConfirm }) => (
  <div className={isMobile ? "delete-popup-div-1" : undefined} style={!isMobile ? { top: "7%", left: "40%", position: "absolute", zIndex: 9, width: "27rem", backdropFilter: "blur(5px)", height: "20rem", boxShadow: "0px 0px 10px rgba(0,0,0,0.1)", borderRadius: 12, border: "2px solid #e97730" } : undefined}>
    <button style={{ zIndex: 9, position: "absolute", backgroundColor: "transparent", border: "none", right: 0 }} onClick={onClose}>
      <FaX style={{ color: "#e97730", fontSize: 19 }} />
    </button>
    <h4 style={{ color: "#09528a", textAlign: "center", position: "absolute", marginTop: "5rem" }}>
      Do you Really Want to DELETE the Machine?
    </h4>
    <div style={{ position: "relative", marginLeft: "10%", marginTop: "45%", display: "flex", gap: "3%" }}>
      <button className={isMobile ? "delete-popup-yes-btn-1" : undefined}
        style={!isMobile ? { width: "10rem", fontWeight: 700, color: "white", height: 55, backgroundColor: "#aaa", outline: "4px solid #aaa", border: "4px solid #ffff", borderColor: "white", borderRadius: 30 } : undefined}
        onClick={onConfirm}>YES</button>
      <button className={isMobile ? "delete-popup-no-btn-1" : undefined}
        style={!isMobile ? { width: "10rem", fontWeight: 700, color: "white", height: 55, backgroundColor: "#e97730", outline: "4px solid #e97730", border: "4px solid #ffff", borderColor: "white", borderRadius: 30 } : undefined}
        onClick={onClose}>NO</button>
    </div>
  </div>
));

const EmptyPlaceholder = memo(({ isMobile, isLoginByParentUser, showPlaceholder, onCreateClick }) => {
  if (!showPlaceholder) return null;
  const isGuest = isLoginByParentUser !== 1;
  return (
    <div style={{ position: "absolute", top: "30%", left: isMobile ? "30%" : isGuest ? "47%" : "48%" }}>
      <figure style={{ width: 100, height: 100, background: "rgb(233,119,48)", borderRadius: "50%", objectFit: "cover", margin: "auto", padding: 20, outline: "3px solid rgb(233,119,48)", border: "5px solid white" }}>
        <img src="/admin/images/admin/menu/vm-white.png" alt="" style={{ marginLeft: -5, width: 60, height: 60 }} />
      </figure>
      <div style={{ zIndex: 10, top: 95, left: isGuest ? 78 : 70, position: "absolute", width: 20, height: 7, backgroundColor: "#fff" }} />
      {isGuest ? (
        <p style={{ width: "11rem", zIndex: 9, position: "relative", textAlign: "center", fontWeight: 700, color: "white", height: 55, backgroundColor: "#e97730", outline: "4px solid #e97730", border: "4px solid #ffff", borderColor: "white", borderRadius: 30, display: "flex", alignItems: "center", justifyContent: "center" }}>
          No Machines Found
        </p>
      ) : (
        <button style={{ width: "10rem", zIndex: 9, position: "relative", fontWeight: 700, color: "white", height: 55, backgroundColor: "#e97730", outline: "4px solid #e97730", border: "4px solid #ffff", borderColor: "white", borderRadius: 30 }} onClick={onCreateClick}>
          Create Machine
        </button>
      )}
    </div>
  );
});

// Single machine card — memoized so it only re-renders when its own item changes
const MachineCard = memo(({
  item, idx, isMobile, shownOnce, machineData, isHovered, deletingMachine,
  machineUser, isLoginByParentUser, setIsHovered,
  onViewStatus, onMonitor, onEdit, onDelete, onToggle, onVNC,
}) => {
  const label = item.vm_status == 0 && item.current_action_status !== "Failed" && !shownOnce
    ? "Standard"
    : getVmLabel(item);
  const diskSize = getDiskSize(item);
  const isProcessing = item.current_action_status == "PROCESSING";
  const isFailed = item.current_action_status == "Failed";
  const hasNwSuspended = machineData?.[idx]?.nw_suspended !== 0;

  // Status icon logic
  const statusIcon = (() => {
    if (item.vm_id === deletingMachine) {
      return <div className="loader-delete" style={{ position: isMobile ? "relative" : "absolute", left: isMobile ? 95 : "12rem", top: isMobile ? 12 : 41, border: "none" }} />;
    }
    if (item.vm_id === null) return <img src="./i-icon.png" alt="" style={{ width: 25, height: 25 }} />;
    if (isProcessing) {
      return <div className="loader-vnc" style={{ position: isMobile ? "absolute" : "relative", height: isMobile ? 10 : 15, width: isMobile ? 20 : 30, left: isMobile ? 34 : 90, top: isMobile ? 36 : 40,bottom: isMobile ? 7 : 40, border: "none", backgroundColor: isMobile ? "transparent" : "grey" }} />;
    }
    if (isFailed) {
      if (!isMobile) return <img src="./i-icon.png" alt="" style={{ width: 25, height: 25 }} />;
      if (machineUser?.force_status === 1) {
        return <button style={{ position: "relative", left: 100, border: "none", backgroundColor: "transparent" }} onClick={() => onVNC(item, true)}><img src="./refresh.png" alt="" style={{ width: 25, height: 25 }} /></button>;
      }
      return <img src="./i-icon.png" alt="" style={{ width: 25, height: 25 }} />;
    }
    if (item.vnc_status !== 0) {
      return <button style={{ position: "relative", left: 100, top: isMobile ? 30 : 35, border: "none", backgroundColor: "transparent" }} onClick={() => onVNC(item)}><img src="./vmc-monitor.png" alt="" style={{ width: 25, height: 25 }} /></button>;
    }
    return null;
  })();

  return (
    <div className="box" style={{ marginRight: isMobile ? undefined : 15, marginTop: isMobile ? "4rem" : 15, position: "relative" }}>
      <div style={{ position: "relative", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", padding: "20px 0px", zIndex: 1 }}>
        <img src="/images/admin/01-home/server.svg" className="bg-image" style={{ position: "absolute", width: "100%", height: isMobile ? "100%" : "105%", left: 0, right: 0, top: 0, zIndex: -1 }} />

        {/* Network suspended */}
        {hasNwSuspended && (
          <div style={{ position: "relative" }}>
            <img
              src={isMobile ? "./i-icon.png" : "./no-wifi.png"}
              alt=""
              style={{ width: isMobile ? 25 : 24, height: isMobile ? 25 : 24, position: "absolute", top: isMobile ? 35 : 25, left: -110 }}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
            {isHovered && (
              <div style={{ position: "absolute", top: -15, left: -110, backgroundColor: "#035189", color: "white", padding: 5, border: "1px solid #035189", borderRadius: 3, boxShadow: "0 2px 4px rgba(0,0,0,0.1)", zIndex: 10 }}>
                {machineData?.[idx]?.nw_suspended_msg}
              </div>
            )}
          </div>
        )}

        {statusIcon}

        {/* Machine icon block */}
        <div className="machine-icon cpu-icon" style={{ marginTop: isMobile ? -57 : -25, marginBottom: isMobile ? 15 : undefined, marginLeft: isMobile ? 0 : undefined }}>
          {isMobile ? (
            <>
              <div className="in-border" style={{ marginLeft: 27, height: 100, width: 100, border: "2px solid #E97730", borderRadius: "50%", backgroundColor: "transparent", padding: 7 }}>
                <div className="in-border" style={{ height: 80, width: 80, padding: "1px 1px 1px 12px", border: "2px solid #E97730", borderRadius: "50%", margin: "auto", backgroundColor: "#E97730" }}>
                  <figure style={{ background: "#e9773000", borderRadius: "50%", padding: "20px 8px 0px 0px", objectFit: "cover", display: "table", margin: "auto" }}>
                    {item.vm_status === 0 && !shownOnce
                      ? <div className="loader-machine-make" style={{ width: 77, marginTop: -22, marginLeft: -12 }} />
                      : <img src="/images/admin/01-home/cpu.svg" alt="" style={{ width: 40, height: 40 }} />}
                  </figure>
                </div>
              </div>
              <Button style={{ marginTop: -2, width: item.vm_type === 1 ? 125 : 142, height: 35, marginLeft: 10, backgroundColor: "transparent", border: "3px solid rgb(233,119,48)", borderRadius: 25 }}>
                <h4 style={{ color: "white", backgroundColor: "rgb(233,119,48)", paddingTop: 4, borderRadius: 15, height: 25, marginTop: -3, fontSize: 14, fontWeight: 700, marginLeft: -12, width: item.vm_type === 1 ? 115 : 130 }}>
                  {label}
                </h4>
              </Button>
            </>
          ) : (
            <>
              <div className="machine-icon-edit-profile" style={{ marginLeft: 34, width: 94, height: 94 }}>
                {item.vm_status == 0 && !isFailed && !shownOnce
                  ? <img src="/images/admin/01-home/cpu.svg" alt="" style={{ width: 40, height: 40 }} />
                  // <div className="loader-machine-make" style={{ width: 94 }} />
                  : <img src="/images/admin/01-home/cpu.svg" alt="" style={{ width: 40, height: 40 }} />}
              </div>
              <div className="machine-titles theme-bg-orange">{label}</div>
            </>
          )}
        </div>

        <div className="machine-name theme-color-blue" style={{ marginTop: isMobile ? -15 : 10 }}>{item.vm_name}</div>
        <div className="machine-config-badge" style={{ color: "white" }}>
          {item.ram / 1024} GB / {diskSize} GB / {item.cpu} vCPU(s)
        </div>
        <div className="line-shape" style={{ width: "95%", height: 2, borderRadius: "50%", marginTop: 10, background: "linear-gradient(90deg,rgba(190,190,190,0),rgba(190,190,190,1),rgba(190,190,190,0))" }} />

        <IpRow label="Location" value={item.location} />
        <IpRow label="Public IP" value={item.public_ip} />
        <IpRow label="Private IP" value={item.ip_address} />

        <div className="machine-date" style={{ marginTop: 8 }}>Created:{item.created_at}</div>

        {/* View Status / Monitor */}
        <div className="actions" style={{ paddingLeft: 20, marginTop: -4, marginBottom: isMobile ? 10 : undefined, display: "flex", justifyContent: "space-around" }}>
          {[{ label: "View Status", onClick: () => onViewStatus(item) }, { label: "Monitor", onClick: () => onMonitor(item) }].map(({ label: lbl, onClick }) => (
            <div key={lbl} className="log-in" style={{ marginBottom: 6, marginTop: 8 }} onClick={onClick}>
              <a className="media-link">
                <div className="media-banner" style={{ minWidth: "6rem", marginTop: 5 }}>
                  <img className="normal-banner" src="/admin/images/admin/wallet/add-money-btn.png" alt="" style={{ width: "6rem" }} />
                  <img className="hover-img-banner" src="/admin/images/admin/wallet/recharge-btn.png" alt="" />
                  <span className="login-text" style={{ color: "white", fontSize: 14, fontWeight: 600 }}>{lbl}</span>
                </div>
              </a>
            </div>
          ))}
        </div>

        {/* Toggle + edit/delete */}
        <div className="actions" style={{ marginTop: -1 }}>
          <div className="icons log-in" style={!isMobile ? { gap: 15 } : {}}>
            <div
              className={`switch ${item.status === 1 ? "on" : "off"}`}
              onClick={() => onToggle(item)}
              style={{
                top: isMobile ? 4 : undefined,
                marginLeft: isMobile ? -30 : undefined,
                cursor: isProcessing ? "not-allowed" : "pointer",
                opacity: isProcessing ? 0.6 : 1,
                transform: !isMobile && isProcessing ? "scale(1.2)" : "scale(1)",
              }}
            >
              <div className="toggle" />
              <div className="labels"><span>ON</span><span>OFF</span></div>
            </div>

            {/* Edit & Delete — always visible */}
            <div onClick={() => onEdit(item)} style={{ cursor: "pointer" }}>
              <a className="" style={isMobile ? { marginLeft: 55, marginBottom: -15, marginRight: 15 } : {}}>
                <img src="/images/admin/01-home/edit.svg" alt="edit" />
              </a>
            </div>
            <div onClick={() => onDelete(item)} style={{ cursor: "pointer" }}>
              <a className="delete" style={isMobile ? { marginBottom: -5, marginRight: -30 } : {}}>
                <img src="/images/admin/01-home/delete.svg" alt="delete" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

// ─── Main Component ───────────────────────────────────────────────────────────
const MyMachinePage = () => {
  const navigate = useNavigate();
  const { dropdownValue } = useDropdown();
  const { smuser, isLoginByParentUser } = useAuth();

  const [loading, setLoading]               = useState(false);
  const [vNCinfo, setVNCinfo]               = useState(null);
  const [machineData, setMachineData]       = useState(null);
  const [vncPopup, SetVncPopup]             = useState(false);
  const [editPopup, SetEditPopup]           = useState(false);
  const [deletePopup, SetDeletePopup]       = useState(false);
  const [showPlaceholder, SetShowPlaceholder] = useState(false);
  const [shownOnce, setShownOnce]           = useState(false);
  const [searchText, setSearchText]         = useState("");
  const [editMachineId, setEditMachineId]   = useState("");
  const [machineUser, setMachineUser]       = useState(null);
  const [deletingMachine, setDeletingMachine] = useState("");
  const [showTermsCondition, setShowTermsCondition] = useState(false);
  const [machineToDelete, setMachineToDelete] = useState(null);
  const [slaDesc, setSLADesc]               = useState(null);
  const [slaId, setSLAid]                   = useState(null);
  const [myMachineId, setMyMachineId]       = useState(null);
  const [isProfileVerified, setProfileVerified] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isHovered, setIsHovered]           = useState(false);
  const [isMobile, setIsMobile]             = useState(isMobileDevice);
  const [showJumpDeletePopup, setShowJumpDeletePopup] = useState(false);
  const [selectedVmId, setSelectedVmId]     = useState(null);

  // ─── Stable API helpers ────────────────────────────────────────────────────
  // These never change, so are safe as useCallback with empty deps
  const apiPost = useCallback(async (url, payload) => {
    const enc = await apiEncryptRequest(payload);
    const res = await instance.post(url, enc);
    return decryptData(res.data);
  }, []);

  const showToast = useCallback((msg) => showAppToast(msg, isMobile), [isMobile]);

  // ─── GetMachines ───────────────────────────────────────────────────────────
  const GetMachines = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiPost("/machines", { user_id: smuser.id });
      setMachineUser(data.user);
      const vmArray = Object.values(data.vm);
      const processing = vmArray.find((v) => v.current_action_status === "PROCESSING");
      SetShowPlaceholder(vmArray.length === 0);
      setMachineData(vmArray);
      // fetch profile details in background — don't block machine render
      GetMyDetails();
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id]); // only re-create if user id changes

  const GetMyDetails = useCallback(async () => {
    try {
      const res = await instance.post("/editprofile", { user_id: smuser.id });
      const user = res.data.user;
      const verified = user.country === "India"
        ? [user.gst_verify, user.aadhar_verify, user.pan_verify, user.driving_verify, user.ciib_verify].some(Boolean)
        : user.identity_verify === 1;
      setProfileVerified(verified);
    } catch (e) {}
  }, [smuser?.id]);

  // ─── Mount: load once + resize listener — NO auto-refresh ────────────────
  useEffect(() => {
    GetMachines();
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); // run once only on mount


  // ─── Scroll restore (no auto-refresh — use manual Refresh button) ─────────
  useEffect(() => {
    const onScroll = () => setScrollPosition(window.scrollY);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { window.scrollTo(0, scrollPosition); }, [scrollPosition]);


  // ─── Machine actions ───────────────────────────────────────────────────────
  const slaManagement = useCallback(async (machineId) => {
    setLoading(true);
    try {
      const data = await apiPost("/slamgnt", { user_id: smuser.id });
      if (data.status) { setShowTermsCondition(true); setSLADesc(data.sla); setSLAid(data.sla_id); setMyMachineId(machineId); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id, apiPost]);

  const [spinning, setSpinning] = useState(false);

  const handleRefresh = () => {
    setSpinning(true);
    setTimeout(() => setSpinning(false), 1000);
    GetMachines();
  };
  // Guard: returns true if action can proceed, false if blocked (and handles side-effects)
  const guardAction = useCallback((item) => {
    if (item.vm_id === null) return false;
    if (item.current_action_status === "PROCESSING") { showToast(PROCESSING_MSG); return false; }
    if (item.sla_status !== 1) {
      if (!isProfileVerified) { navigate("/edit-profile"); return false; }
      if (item.public_ip !== null) { slaManagement(item.vm_id); return false; }
      showToast(PROCESSING_MSG);
      return false;
    }
    return true;
  }, [isProfileVerified, showToast, navigate, slaManagement]);

  const toggleSwitch = useCallback(async ({ id, status }) => {
    setLoading(true);
    try {
      const url = status === "on" ? "/vm/on" : "/vm/off";
      const data = await apiPost(url, { user_id: smuser.id, vm_id: id });
      if (data.status) GetMachines();
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id, apiPost, GetMachines]);

  const RebuildMachine = useCallback(async (machineItem) => {
    if (!machineItem) { showToast("Oops! Your wallet balance is low, Please Add Money to create machine"); return; }
    setLoading(true);
    const payload = { user_id: smuser.id, request_id: machineItem.request_id, machine_price: machineItem.machine_o_rate, plan_time: machineItem.plan_time, vm_template: machineItem.vm_template, osiddata: machineItem.osiddata, name: machineItem.vm_name, cpu: machineItem.cpu, ram: machineItem.ram, hard_disk: machineItem.hard_disk, data_transfer: machineItem.data_transfer };
    try {
      const enc = await apiEncryptRequest(payload);
      const res = await instance.post("/vm/rebuild_new", enc);
      const data = await decryptData(res.data);
      if (smuser.platform_status === "1") { if (data.status) window.location.href = "/vm-machine"; }
      else { setTimeout(() => { window.location.href = "/vm-machine"; setLoading(false); }, 3000); }
    } catch { showToast("Oops! Something went wrong. Please try again later or contact support."); }
  }, [smuser, showToast, apiPost]);

  const deleteMachine = useCallback(async (machine) => {
    setDeletingMachine(machine.vm_id);
    try {
      const response = await apiPost("/vm/distroy", { user_id: smuser.id, vm_id: machine.vm_id, id: machine.id });
      if (response.type === "jump_attached") { setSelectedVmId(machine.vm_id); setShowJumpDeletePopup(true); return; }
      if (response.status) GetMachines();
      else toast.error("Unable To Delete Machine Please Try Again Later!");
    } catch (e) { console.error(e); }
    setDeletingMachine(null);
  }, [smuser?.id, apiPost, GetMachines]);

  const VMC = useCallback(async (machineId) => {
    setLoading(true);
    try {
      const res = await instance.post("vm/vnc", { user_id: smuser.id, vm_id: machineId });
      if (res.data.status) window.location.href = res.data.url;
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id]);

  const agreeSLAPdf = useCallback(async (machineid) => {
    setLoading(true);
    try {
      const data = await apiPost("/termsaccept", { user_id: smuser.id, vm_id: machineid, sla_id: slaId, finger_print: window.navigator.userAgent });
      if (data.success) { GetMachines(); showToast("SLA terms accepted successfully."); }
      else showToast(data.message);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [smuser?.id, slaId, apiPost, GetMachines, showToast]);

  // ─── Card-level action handlers (passed as props to MachineCard) ───────────
  const handleViewStatus = useCallback((item) => {
    if (!guardAction(item)) return;
    if (item.current_action_status === "PROCESSING" || item.current_action_status === "Failed") { showToast(PROCESSING_MSG); return; }
    navigate("/vm/status", { state: { vmDetails: item.vm_id } });
  }, [guardAction, showToast, navigate]);

  const handleMonitor = useCallback((item) => {
    if (!guardAction(item)) return;
    if (item.current_action_status === "PROCESSING" || item.current_action_status === "Failed") { showToast(PROCESSING_MSG); return; }
    navigate("/monitor-graph", { state: { vm_data: item.vm_id } });
  }, [guardAction, showToast, navigate]);

  const handleEdit = useCallback((item) => {
    if (item.vm_id === null) return;
    if (item.current_action_status === "PROCESSING") { showToast(PROCESSING_MSG); return; }
    if (item.sla_status === 1) { setEditMachineId(item.vm_id); SetEditPopup(true); return; }
    if (!isProfileVerified) { navigate("/edit-profile"); return; }
    item.public_ip !== null ? slaManagement(item.vm_id) : showToast(PROCESSING_MSG);
  }, [isProfileVerified, slaManagement, showToast, navigate]);

  const handleDelete = useCallback((item) => {
    if (item.current_action_status === "PROCESSING") { showToast(PROCESSING_MSG); return; }
    if (item.vm_id !== null && item.sla_status !== 1) {
      if (!isProfileVerified) { navigate("/edit-profile"); return; }
      item.public_ip !== null ? slaManagement(item.vm_id) : showToast(PROCESSING_MSG);
      return;
    }
    SetDeletePopup(true);
    setMachineToDelete({ vm_id: item.vm_id, id: item.id });
  }, [isProfileVerified, slaManagement, showToast, navigate]);

  const handleToggle = useCallback((item) => {
    if (item.vm_id === null) return;
    if (item.current_action_type === "POWEROFF") {
      toggleSwitch({ id: item.vm_id, status: item.status === 1 ? "off" : "on" }); return;
    }
    if (item.current_action_status === "PROCESSING") { showToast("Server configuration in Progress. Please Return after 15 Minutes from the Creation Time"); return; }
    if (item.sla_status === 1) {
      if (item.current_action_status !== "Failed") toggleSwitch({ id: item.vm_id, status: item.status === 1 ? "off" : "on" });
      else showToast("⚠️ Server configuration failed. Please check again later.");
    } else {
      if (!isProfileVerified) { navigate("/edit-profile"); return; }
      item.public_ip !== null ? slaManagement(item.vm_id) : showToast("Server configuration in progress. Please return after 15 minutes.");
    }
  }, [isProfileVerified, slaManagement, showToast, navigate, toggleSwitch]);

  const handleVNC = useCallback((item, isRebuild = false) => {
    if (isRebuild) { RebuildMachine(item); return; }
    if (item.vm_id === null) return;
    if (item.sla_status === 1) { VMC(item.vm_id); return; }
    if (!isProfileVerified) { navigate("/edit-profile"); return; }
    if (item.public_ip !== null) slaManagement(item.vm_id);
  }, [isProfileVerified, VMC, RebuildMachine, slaManagement, navigate]);

  // ─── Filtered + searched list (memoized — only recomputes when deps change) ─
  const visibleMachines = useMemo(() => {
    if (!machineData) return [];
    const byLocation = dropdownValue === "Global" ? machineData : machineData.filter((m) => m.location === dropdownValue);
    return searchText ? byLocation.filter((m) => m.vm_name.toLowerCase().includes(searchText.toLowerCase())) : byLocation;
  }, [machineData, dropdownValue, searchText]);

  const hasMachines = machineData && machineData.length > 0;

  // Shared card props (stable object reference via useMemo)
  const cardSharedProps = useMemo(() => ({
    isMobile, shownOnce, machineData, isHovered, deletingMachine,
    machineUser, isLoginByParentUser, setIsHovered,
    onViewStatus: handleViewStatus, onMonitor: handleMonitor,
    onEdit: handleEdit, onDelete: handleDelete,
    onToggle: handleToggle, onVNC: handleVNC,
  }), [isMobile, shownOnce, machineData, isHovered, deletingMachine, machineUser,
      isLoginByParentUser, handleViewStatus, handleMonitor, handleEdit,
      handleDelete, handleToggle, handleVNC]);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ width: "100%", minHeight: "65rem", position: "relative", backgroundImage: "url(./main-bg.jpg)", backgroundSize: "cover", backgroundRepeat: "round", backgroundBlendMode: "overlay" }}>
      {loading && <LoaderOverlay />}

      {showTermsCondition && (
        <SlaPopup isMobile={isMobile} slaDesc={slaDesc} myMachineId={myMachineId}
          onClose={() => setShowTermsCondition(false)} onAgree={agreeSLAPdf} />
      )}
      {showJumpDeletePopup && (
        <JumpDeletePopup isMobile={isMobile}
          onClose={() => setShowJumpDeletePopup(false)}
          onViewVmList={() => { setShowJumpDeletePopup(false); navigate("/vm/status", { state: { vmDetails: selectedVmId } }); }} />
      )}

      {isMobile ? (
        <div style={{ height: "50rem" }}>
          {vncPopup && <VncPopup isMobile vNCinfo={vNCinfo} onClose={() => SetVncPopup(false)} />}
          <div style={{ position: "relative" }}>
            <div className="heading-dotted-machine">VM List <span /></div>
          <>
            <style>{`
              @keyframes spin360 {
                from { transform: rotate(0deg); }
                to   { transform: rotate(360deg); }
              }
              @keyframes ripple-refresh {
                to { width: 200px; height: 200px; opacity: 0; }
              }
              .refresh-btn {
                position: absolute;
                right: ${isMobile ? "16px" : "5rem"};
                top: ${isMobile ? "0.5rem" : "5.5rem"};
                display: flex;
                align-items: center;
                gap: 8px;
                background: linear-gradient(145deg, #0a6abf, #035189, #023d6b);
                color: white;
                border: none;
                border-radius: 30px;
                padding: 10px 22px;
                font-weight: 700;
                font-size: 14px;
                cursor: pointer;
                z-index: 10;
                overflow: hidden;
                box-shadow:
                  0 6px 20px rgba(3,81,137,0.45),
                  0 2px 4px rgba(0,0,0,0.3),
                  inset 0 1px 0 rgba(255,255,255,0.15),
                  inset 0 -2px 0 rgba(0,0,0,0.2);
                transition: transform 0.15s, box-shadow 0.15s;
                letter-spacing: 0.04em;
              }
              .refresh-btn:hover {
                transform: translateY(-2px) scale(1.03);
                box-shadow:
                  0 10px 28px rgba(3,81,137,0.55),
                  0 4px 8px rgba(0,0,0,0.25),
                  inset 0 1px 0 rgba(255,255,255,0.2),
                  inset 0 -2px 0 rgba(0,0,0,0.2);
              }
              .refresh-btn:active {
                transform: translateY(1px) scale(0.97);
                box-shadow:
                  0 3px 10px rgba(3,81,137,0.4),
                  inset 0 2px 4px rgba(0,0,0,0.2);
              }
              .refresh-icon {
                font-size: 16px;
                display: inline-block;
                animation: ${spinning ? "spin360 0.8s cubic-bezier(0.4,0,0.2,1)" : "none"};
              }
              .refresh-ripple {
                position: absolute;
                width: 0; height: 0;
                background: rgba(255,255,255,0.25);
                border-radius: 50%;
                transform: translate(-50%, -50%);
                animation: ripple-refresh 0.7s ease-out forwards;
                pointer-events: none;
              }
              .refresh-shine {
                position: absolute;
                top: 0; left: -75%;
                width: 50%; height: 100%;
                background: linear-gradient(
                  120deg,
                  transparent 0%,
                  rgba(255,255,255,0.12) 50%,
                  transparent 100%
                );
                pointer-events: none;
                border-radius: 30px;
              }
            `}</style>

          <button
            className="refresh-btn"
            onClick={(e) => {
              // ripple
              const btn  = e.currentTarget;
              const rect = btn.getBoundingClientRect();
              const rip  = document.createElement("span");
              rip.className = "refresh-ripple";
              rip.style.left = `${e.clientX - rect.left}px`;
              rip.style.top  = `${e.clientY - rect.top}px`;
              btn.appendChild(rip);
              setTimeout(() => rip.remove(), 700);
              handleRefresh();
            }}
            title="Refresh VM list"
          >
            {/* Shine overlay */}
            <span className="refresh-shine" />

            {/* Spinning icon */}
            <span
              className="refresh-icon"
              style={{
                display: "inline-block",
                animation: spinning ? "spin360 0.8s cubic-bezier(0.4,0,0.2,1)" : "none",
              }}
            >
              ⟳
            </span>

            {spinning ? "Refreshing..." : "Refresh"}
          </button>
        </>
          </div>
          {hasMachines ? (
            <>
              {editPopup && <EditPopup isMobile onClose={() => SetEditPopup(false)}
                onEditOs={() => navigate("/edit-vm", { state: { machineData: editMachineId, editType: "os" } })}
                onEditConfig={() => navigate("/edit-vm", { state: { machineData: editMachineId, editType: "config" } })} />}
              {deletePopup && <DeletePopup isMobile onClose={() => SetDeletePopup(false)}
                onConfirm={() => { SetDeletePopup(false); deleteMachine(machineToDelete); }} />}

              <div className="input-container input-container-1">
                <input type="text" name="search" className="input-signup input-tickets" placeholder="Search Machine"
                  value={searchText} style={{ fontSize: 18, color: "black", textAlign: "center", width: 10 }}
                  onChange={(e) => setSearchText(e.target.value)} />
                {searchText && (
                  <button style={{ backgroundColor: "transparent", border: "none" }} onClick={() => setSearchText("")}>
                    <FaX style={{ color: "#154e7a", fontSize: 19 }} />
                  </button>
                )}
              </div>
              <div className="wallet-container" style={{ border: "none" }}>
                <div className="wallet-box">
                  {visibleMachines.map((item, idx) => (
                    <MachineCard key={item.vm_id ?? idx} item={item} idx={idx} {...cardSharedProps} />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <EmptyPlaceholder isMobile isLoginByParentUser={isLoginByParentUser} showPlaceholder={showPlaceholder} onCreateClick={() => navigate("/vm/create")} />
          )}
        </div>
      ) : (
        <div className="features-page-solution" style={{ height: "100%", padding: "5rem" }}>
          {vncPopup && <VncPopup isMobile={false} vNCinfo={vNCinfo} onClose={() => SetVncPopup(false)} />}
          <div style={{ position: "relative" }}>
            <div className="heading-dotted-machine">VM List <span /></div>
                      <>
                <style>{`
                  @keyframes spin360 {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                  }
                  @keyframes ripple-refresh {
                    to { width: 200px; height: 200px; opacity: 0; }
                  }
                  .refresh-btn {
                    position: absolute;
                    right: ${isMobile ? "16px" : "5rem"};
                    top: ${isMobile ? "0.5rem" : "5.5rem"};
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: linear-gradient(145deg, #0a6abf, #035189, #023d6b);
                    color: white;
                    border: none;
                    border-radius: 30px;
                    padding: 10px 22px;
                    font-weight: 700;
                    font-size: 14px;
                    cursor: pointer;
                    z-index: 10;
                    overflow: hidden;
                    box-shadow:
                      0 6px 20px rgba(3,81,137,0.45),
                      0 2px 4px rgba(0,0,0,0.3),
                      inset 0 1px 0 rgba(255,255,255,0.15),
                      inset 0 -2px 0 rgba(0,0,0,0.2);
                    transition: transform 0.15s, box-shadow 0.15s;
                    letter-spacing: 0.04em;
                  }
                  .refresh-btn:hover {
                    transform: translateY(-2px) scale(1.03);
                    box-shadow:
                      0 10px 28px rgba(3,81,137,0.55),
                      0 4px 8px rgba(0,0,0,0.25),
                      inset 0 1px 0 rgba(255,255,255,0.2),
                      inset 0 -2px 0 rgba(0,0,0,0.2);
                  }
                  .refresh-btn:active {
                    transform: translateY(1px) scale(0.97);
                    box-shadow:
                      0 3px 10px rgba(3,81,137,0.4),
                      inset 0 2px 4px rgba(0,0,0,0.2);
                  }
                  .refresh-icon {
                    font-size: 16px;
                    display: inline-block;
                    animation: ${spinning ? "spin360 0.8s cubic-bezier(0.4,0,0.2,1)" : "none"};
                  }
                  .refresh-ripple {
                    position: absolute;
                    width: 0; height: 0;
                    background: rgba(255,255,255,0.25);
                    border-radius: 50%;
                    transform: translate(-50%, -50%);
                    animation: ripple-refresh 0.7s ease-out forwards;
                    pointer-events: none;
                  }
                  .refresh-shine {
                    position: absolute;
                    top: 0; left: -75%;
                    width: 50%; height: 100%;
                    background: linear-gradient(
                      120deg,
                      transparent 0%,
                      rgba(255,255,255,0.12) 50%,
                      transparent 100%
                    );
                    pointer-events: none;
                    border-radius: 30px;
                  }
                `}</style>

                <button
                  className="refresh-btn"
                  onClick={(e) => {
                    // ripple
                    const btn  = e.currentTarget;
                    const rect = btn.getBoundingClientRect();
                    const rip  = document.createElement("span");
                    rip.className = "refresh-ripple";
                    rip.style.left = `${e.clientX - rect.left}px`;
                    rip.style.top  = `${e.clientY - rect.top}px`;
                    btn.appendChild(rip);
                    setTimeout(() => rip.remove(), 700);
                    handleRefresh();
                  }}
                  title="Refresh VM list"
                >
                  {/* Shine overlay */}
                  <span className="refresh-shine" />

                  {/* Spinning icon */}
                  <span
                    className="refresh-icon"
                    style={{
                      display: "inline-block",
                      animation: spinning ? "spin360 0.8s cubic-bezier(0.4,0,0.2,1)" : "none",
                    }}
                  >
                    ⟳
                  </span>

                  {spinning ? "Refreshing..." : "Refresh"}
                </button>
              </>
          </div>

          {hasMachines && (
            <div className="input-container" style={{ marginLeft: "85%", position: "relative", border: "2px solid #035189", color: "#035189", width: "15rem", marginTop: -72, height: 55 }}>
              <input type="text" name="search" className="input-signup input-tickets" placeholder="Search Machine"
                value={searchText} style={{ fontSize: 24, color: "#035189", textAlign: "center", width: "100%" }}
                onChange={(e) => setSearchText(e.target.value)} />
              {searchText && (
                <button style={{ backgroundColor: "white", border: "none", color: "#035189" }} onClick={() => setSearchText("")}>
                  <FaX style={{ color: "#154e7a", fontSize: 19 }} />
                </button>
              )}
            </div>
          )}

          <div className="features-section-solution">
            <Row>
              <div className="col-md-1" />
              <div className="col-md-10">
                {hasMachines ? (
                  <>
                    {editPopup && <EditPopup isMobile={false} onClose={() => SetEditPopup(false)}
                      onEditOs={() => navigate("/edit-vm", { state: { machineData: editMachineId, editType: "os" } })}
                      onEditConfig={() => navigate("/edit-vm", { state: { machineData: editMachineId, editType: "config" } })} />}
                    {deletePopup && <DeletePopup isMobile={false} onClose={() => SetDeletePopup(false)}
                      onConfirm={() => { SetDeletePopup(false); deleteMachine(machineToDelete); }} />}
                    <div className="wallet-container" style={{ border: "none", marginLeft: 30 }}>
                      <div className="wallet-box" style={{ justifyContent: "unset", alignContent: "flex-start", flexDirection: "row", flexWrap: "wrap", display: "flex", gap: 20 }}>
                        {visibleMachines.map((item, idx) => (
                          <MachineCard key={item.vm_id ?? idx} item={item} idx={idx} {...cardSharedProps} />
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <EmptyPlaceholder isMobile={false} isLoginByParentUser={isLoginByParentUser} showPlaceholder={showPlaceholder} onCreateClick={() => navigate("/vm/create")} />
                )}
              </div>
              <div className="col-md-1" />
            </Row>
          </div>
        </div>
      )}

      <div className="apptoast-align" style={{ zIndex: 9999999999 }}>
        <Toaster position={isMobile ? "top-center" : "bottom-right"} reverseOrder={false} />
      </div>
    </div>
  );
};

export default MyMachinePage;